import React from 'react';
import styled, { keyframes } from 'styled-components';
import codewaveLogo from '../assets/codewave-logo.png';

const FullPageLoader = ({ title = "Codewave Technologies", subtitle = "Empowering businesses with technology", showLogo = true }) => {
  return (
    <LoaderContainer>
      <LoaderContent>
        <LogoContainer>
          {showLogo ? (
            <LogoImage src={codewaveLogo} alt="Codewave Technologies" />
          ) : (
            <>
              <LogoText>{title}</LogoText>
              <SubtitleText>{subtitle}</SubtitleText>
            </>
          )}
        </LogoContainer>
        
        <SpinnerContainer>
          <Spinner>
            <SpinnerRing />
            <SpinnerRing />
            <SpinnerRing />
          </Spinner>
        </SpinnerContainer>
        
        <LoadingText>Loading...</LoadingText>
      </LoaderContent>
      
      <BackgroundPattern>
        <PatternDot />
        <PatternDot />
        <PatternDot />
        <PatternDot />
        <PatternDot />
        <PatternDot />
        <PatternDot />
        <PatternDot />
        <PatternDot />
      </BackgroundPattern>
    </LoaderContainer>
  );
};

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.7;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const rotate = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

// Styled Components
const LoaderContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #f5f7fa 0%, #e9edf5 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  overflow: hidden;
`;

const LoaderContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  z-index: 2;
  animation: ${fadeIn} 0.8s ease-out;
`;

const LogoContainer = styled.div`
  margin-bottom: 40px;
  animation: ${pulse} 2s ease-in-out infinite;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const LogoImage = styled.img`
  height: 80px;
  width: auto;
  animation: ${pulse} 2s ease-in-out infinite;
  filter: drop-shadow(0 8px 16px rgba(0, 0, 0, 0.1));
  transition: all 0.3s ease;
  
  @media (max-width: 768px) {
    height: 60px;
  }
  
  @media (max-width: 576px) {
    height: 50px;
  }
`;

const LogoText = styled.h1`
  font-size: 48px;
  font-weight: 700;
  margin: 0 0 10px 0;
  background: linear-gradient(
    90deg,
    #105090 0%,
    #2090B0 25%,
    #105090 50%,
    #2090B0 75%,
    #105090 100%
  );
  background-size: 200% auto;
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
  -webkit-text-fill-color: transparent;
  animation: ${shimmer} 3s ease infinite;
  
  @media (max-width: 768px) {
    font-size: 36px;
  }
  
  @media (max-width: 576px) {
    font-size: 28px;
  }
`;

const SubtitleText = styled.p`
  font-size: 18px;
  font-weight: 400;
  color: #666666;
  margin: 0;
  line-height: 1.4;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
  
  @media (max-width: 576px) {
    font-size: 14px;
  }
`;

const SpinnerContainer = styled.div`
  margin-bottom: 30px;
  animation: ${float} 3s ease-in-out infinite;
`;

const Spinner = styled.div`
  position: relative;
  width: 60px;
  height: 60px;
`;

const SpinnerRing = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 3px solid transparent;
  border-top: 3px solid #2090B0;
  border-radius: 50%;
  animation: ${rotate} 1.5s linear infinite;
  
  &:nth-child(2) {
    width: 80%;
    height: 80%;
    top: 10%;
    left: 10%;
    border-top-color: #105090;
    animation-duration: 2s;
    animation-direction: reverse;
  }
  
  &:nth-child(3) {
    width: 60%;
    height: 60%;
    top: 20%;
    left: 20%;
    border-top-color: #2090B0;
    animation-duration: 1s;
  }
`;

const LoadingText = styled.p`
  font-size: 16px;
  font-weight: 500;
  color: #105090;
  margin: 0;
  animation: ${pulse} 1.5s ease-in-out infinite;
  
  @media (max-width: 576px) {
    font-size: 14px;
  }
`;

const BackgroundPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  opacity: 0.1;
`;

const PatternDot = styled.div`
  position: absolute;
  width: 8px;
  height: 8px;
  background: #105090;
  border-radius: 50%;
  animation: ${float} 4s ease-in-out infinite;
  
  &:nth-child(1) {
    top: 20%;
    left: 10%;
    animation-delay: 0s;
  }
  
  &:nth-child(2) {
    top: 30%;
    right: 15%;
    animation-delay: 0.5s;
  }
  
  &:nth-child(3) {
    top: 60%;
    left: 20%;
    animation-delay: 1s;
  }
  
  &:nth-child(4) {
    top: 70%;
    right: 25%;
    animation-delay: 1.5s;
  }
  
  &:nth-child(5) {
    top: 40%;
    left: 50%;
    animation-delay: 2s;
  }
  
  &:nth-child(6) {
    top: 80%;
    left: 70%;
    animation-delay: 2.5s;
  }
  
  &:nth-child(7) {
    top: 15%;
    left: 80%;
    animation-delay: 3s;
  }
  
  &:nth-child(8) {
    top: 50%;
    left: 5%;
    animation-delay: 3.5s;
  }
  
  &:nth-child(9) {
    top: 85%;
    left: 40%;
    animation-delay: 4s;
  }
`;

export default FullPageLoader;
